import React, { useState, useCallback, useMemo } from 'react';
import { ChevronsUp, Search, Plus } from 'lucide-react';
import { Box, Typography, Button, TextField, useTheme, CircularProgress } from '@mui/material';
import { ProjectHierarchyNode } from './ProjectHierarchyNode';
import { ProjectHierarchyContextMenu } from './ProjectHierarchyContextMenu';
import { ProjectHierarchyDialog } from './ProjectHierarchyDialog';
import {
  useFetchProjectHierarchy,
  useDeleteProjectHierarchy,
  useUpdateProjectHierarchy,
} from '../../hooks/useFetchProjects';
import {
  ProjectHierarchy as ProjectHierarchyType,
  GetProjectResponse,
} from '../../api/protosCompiled/project/project_pb';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';

interface ProjectHierarchyProps {
  selectedHierarchyId?: string | null;
  onHierarchySelect?: (hierarchyId: string | null) => void;
  userId: string;
  projects?: GetProjectResponse.AsObject[];
  onProjectClick?: (project: GetProjectResponse.AsObject) => void;
  onProjectEdit?: (project: GetProjectResponse.AsObject) => void;
  onCreateProject?: (hierarchyId: string) => void;
}

export const ProjectHierarchy: React.FC<ProjectHierarchyProps> = ({
  selectedHierarchyId,
  onHierarchySelect,
  userId,
  projects = [],
  onProjectClick,
  onProjectEdit,
  onCreateProject,
}) => {
  const theme = useTheme();

  // Store state
  const expandedNodes = useStore(useUnityBuildStore, state => state.expandedHierarchyNodes);
  const setExpandedNodes = useStore(useUnityBuildStore, state => state.setExpandedHierarchyNodes);
  const toggleHierarchyNode = useStore(useUnityBuildStore, state => state.toggleHierarchyNode);
  const clearExpandedHierarchyNodes = useStore(
    useUnityBuildStore,
    state => state.clearExpandedHierarchyNodes
  );

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(
    selectedHierarchyId ?? null
  );

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    node: ProjectHierarchyType.AsObject | null;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const anchorRef = React.useRef<HTMLElement | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [dialogParentId, setDialogParentId] = useState<string | null>(null);
  const [dialogNode, setDialogNode] = useState<ProjectHierarchyType.AsObject | null>(null);

  // Drag and drop state
  const [draggedNode, setDraggedNode] = useState<ProjectHierarchyType.AsObject | null>(null);
  const [dragOverNode, setDragOverNode] = useState<string | null>(null);

  // Use external selectedId if provided, otherwise use internal state
  const selectedId = selectedHierarchyId !== undefined ? selectedHierarchyId : internalSelectedId;

  // Fetch hierarchy data
  const { data: hierarchyList, error, isLoading } = useFetchProjectHierarchy(userId);
  const deleteMutation = useDeleteProjectHierarchy(userId);
  const updateMutation = useUpdateProjectHierarchy(userId);

  // Reset expanded nodes to only include root nodes if all nodes are expanded
  const hasResetExpandedNodes = React.useRef(false);
  React.useEffect(() => {
    if (hierarchyList && hierarchyList.length > 0 && !hasResetExpandedNodes.current) {
      const allNodeIds = hierarchyList.map(node => node.id);
      const allNodesExpanded = allNodeIds.every(nodeId => expandedNodes.has(nodeId));

      if (allNodesExpanded) {
        // Reset to only expand root nodes
        const rootNodes = hierarchyList.filter(node => !node.parentid || node.parentid === '');
        const rootNodeIds = rootNodes.map(node => node.id);
        setExpandedNodes(new Set(rootNodeIds));
        hasResetExpandedNodes.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyList, setExpandedNodes]); // Intentionally omitting expandedNodes to prevent infinite loop

  // Function to get projects for a specific hierarchy node
  const getProjectsForNode = useCallback(
    (nodeId: string): GetProjectResponse.AsObject[] => {
      // Find the hierarchy node and return its projects
      const hierarchyNode = hierarchyList?.find(h => h.id === nodeId);
      if (!hierarchyNode || !hierarchyNode.projectsList) {
        return [];
      }

      // Convert Project[] to GetProjectResponse.AsObject[]
      const projects = hierarchyNode.projectsList.map(project => ({
        project: project,
      }));

      return projects;
    },
    [hierarchyList]
  );

  // Build tree structure from flat list
  const hierarchyTree = useMemo(() => {
    if (!hierarchyList) {
      return [];
    }

    // If no search term, return full tree
    if (!searchTerm) {
      const buildTree = (parentId: string | null = null): ProjectHierarchyType.AsObject[] => {
        const isRoot = parentId === null || parentId === '';
        const children = hierarchyList.filter(item => {
          if (isRoot) {
            return item.parentid === '' || item.parentid === null;
          }
          return item.parentid === parentId;
        });
        return children.map(item => ({
          ...item,
          children: buildTree(item.id),
        }));
      };
      return buildTree();
    }

    // Search functionality that includes child nodes and their parents
    const searchTermLower = searchTerm.toLowerCase();

    // Find all nodes that match the search term
    const matchingNodes = hierarchyList.filter(
      h =>
        h.name.toLowerCase().includes(searchTermLower) ||
        h.code.toLowerCase().includes(searchTermLower)
    );

    // Find all parent nodes of matching nodes to include them in the tree
    const getParentPath = (nodeId: string): string[] => {
      const node = hierarchyList.find(h => h.id === nodeId);
      if (!node || !node.parentid) return [nodeId];
      return [...getParentPath(node.parentid), nodeId];
    };

    // Get all nodes that should be included (matching nodes + their parents)
    const nodesToInclude = new Set<string>();
    matchingNodes.forEach(node => {
      const path = getParentPath(node.id);
      path.forEach(nodeId => nodesToInclude.add(nodeId));
    });

    // Filter the hierarchy list to only include relevant nodes
    const filteredList = hierarchyList.filter(h => nodesToInclude.has(h.id));

    // Build tree structure
    const buildTree = (parentId: string | null = null): ProjectHierarchyType.AsObject[] => {
      const isRoot = parentId === null || parentId === '';
      const children = filteredList.filter(item => {
        if (isRoot) {
          return item.parentid === '' || item.parentid === null;
        }
        return item.parentid === parentId;
      });
      return children.map(item => ({
        ...item,
        children: buildTree(item.id),
      }));
    };

    const tree = buildTree();
    return tree;
  }, [hierarchyList, searchTerm]);

  // Auto-expand matching nodes when searching
  React.useEffect(() => {
    if (searchTerm && hierarchyList) {
      const searchTermLower = searchTerm.toLowerCase();
      const matchingNodes = hierarchyList.filter(
        h =>
          h.name.toLowerCase().includes(searchTermLower) ||
          h.code.toLowerCase().includes(searchTermLower)
      );

      if (matchingNodes.length > 0) {
        const newExpandedNodes = new Set(expandedNodes);
        matchingNodes.forEach(node => {
          // Add the matching node and all its parents to expanded nodes
          const getParentPath = (nodeId: string): string[] => {
            const node = hierarchyList.find(h => h.id === nodeId);
            if (!node || !node.parentid) return [nodeId];
            return [...getParentPath(node.parentid), nodeId];
          };
          const path = getParentPath(node.id);
          path.forEach(nodeId => newExpandedNodes.add(nodeId));
        });

        // Only update if there are new nodes to expand
        const hasNewNodes = Array.from(newExpandedNodes).some(nodeId => !expandedNodes.has(nodeId));
        if (hasNewNodes) {
          setExpandedNodes(newExpandedNodes);
        }
      }
    }
  }, [searchTerm, hierarchyList, expandedNodes, setExpandedNodes]);

  // Auto-expand parent nodes when a node is selected
  const expandToNode = useCallback(
    (nodeId: string) => {
      if (!hierarchyList) return;

      const findPath = (id: string, currentPath: string[] = []): string[] | null => {
        const node = hierarchyList.find(h => h.id === id);
        if (!node) return null;

        const newPath = [node.id, ...currentPath];

        if (node.parentid) {
          const parentPath = findPath(node.parentid, newPath);
          return parentPath;
        }

        return newPath;
      };

      const path = findPath(nodeId);
      if (path) {
        const newSet = new Set(expandedNodes);
        path.forEach(id => newSet.add(id));
        setExpandedNodes(newSet);
      }
    },
    [hierarchyList, expandedNodes, setExpandedNodes]
  );

  // Handlers
  const handleToggleExpand = useCallback(
    (nodeId: string) => {
      toggleHierarchyNode(nodeId);
    },
    [toggleHierarchyNode]
  );

  const handleSelectNode = useCallback(
    (nodeId: string) => {
      if (onHierarchySelect) {
        onHierarchySelect(nodeId);
      } else {
        setInternalSelectedId(nodeId);
      }
      expandToNode(nodeId);
    },
    [onHierarchySelect, expandToNode]
  );

  const handleCollapseAll = useCallback(() => {
    clearExpandedHierarchyNodes();
    // Clear selected hierarchy when collapsing all
    if (onHierarchySelect) {
      onHierarchySelect(null);
    } else {
      setInternalSelectedId(null);
    }
  }, [onHierarchySelect, clearExpandedHierarchyNodes]);

  // Clean up existing anchor element
  const cleanupAnchor = useCallback(() => {
    if (anchorRef.current && anchorRef.current.parentNode) {
      try {
        document.body.removeChild(anchorRef.current);
      } catch (error) {
        // Element may have already been removed
        console.warn('Anchor element already removed:', error);
      }
      anchorRef.current = null;
    }
  }, []);

  // Context menu handlers
  const handleContextMenu = useCallback(
    (event: React.MouseEvent, node: ProjectHierarchyType.AsObject) => {
      event.preventDefault();
      event.stopPropagation();

      // Clean up any existing anchor
      cleanupAnchor();

      // Create a temporary anchor element at the cursor position
      const tempAnchor = document.createElement('div');
      tempAnchor.style.position = 'fixed';
      tempAnchor.style.top = `${event.clientY}px`;
      tempAnchor.style.left = `${event.clientX}px`;
      tempAnchor.style.width = '1px';
      tempAnchor.style.height = '1px';
      tempAnchor.style.pointerEvents = 'none';
      tempAnchor.style.zIndex = '9999';
      document.body.appendChild(tempAnchor);

      anchorRef.current = tempAnchor;
      setAnchorEl(tempAnchor);
      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        node,
      });
    },
    [cleanupAnchor]
  );

  const handleEllipsisClick = useCallback(
    (event: React.MouseEvent, node: ProjectHierarchyType.AsObject) => {
      event.preventDefault();
      event.stopPropagation();

      // Clean up any existing anchor
      cleanupAnchor();

      // Create a temporary anchor element at the cursor position
      const tempAnchor = document.createElement('div');
      tempAnchor.style.position = 'fixed';
      tempAnchor.style.top = `${event.clientY}px`;
      tempAnchor.style.left = `${event.clientX}px`;
      tempAnchor.style.width = '1px';
      tempAnchor.style.height = '1px';
      tempAnchor.style.pointerEvents = 'none';
      tempAnchor.style.zIndex = '9999';
      document.body.appendChild(tempAnchor);

      anchorRef.current = tempAnchor;
      setAnchorEl(tempAnchor);
      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        node,
      });
    },
    [cleanupAnchor]
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
    cleanupAnchor();
    setAnchorEl(null);
  }, [cleanupAnchor]);

  // Dialog handlers
  const handleOpenDialog = useCallback(
    (mode: 'create' | 'edit', node?: ProjectHierarchyType.AsObject, parentId?: string | null) => {
      setDialogMode(mode);
      setDialogNode(node || null);
      setDialogParentId(parentId || null);
      setDialogOpen(true);
      handleCloseContextMenu();
    },
    [handleCloseContextMenu]
  );

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDialogNode(null);
    setDialogParentId(null);
  }, []);

  // Context menu actions
  const handleAddSubfolder = useCallback(() => {
    if (contextMenu?.node) {
      handleOpenDialog('create', undefined, contextMenu.node.id);
    }
  }, [contextMenu, handleOpenDialog]);

  const handleEdit = useCallback(() => {
    if (contextMenu?.node) {
      handleOpenDialog('edit', contextMenu.node);
    }
  }, [contextMenu, handleOpenDialog]);

  const handleDelete = useCallback(async () => {
    if (contextMenu?.node) {
      const hasChildren = hierarchyList?.some(h => h.parentid === contextMenu.node?.id);
      const message = hasChildren
        ? `Are you sure you want to delete "${contextMenu.node.name}"? This will also delete all subfolders and cannot be undone.`
        : `Are you sure you want to delete "${contextMenu.node.name}"?`;

      if (window.confirm(message)) {
        try {
          await deleteMutation.mutateAsync(contextMenu.node.id);
        } catch (error) {
          console.error('Error deleting hierarchy item:', error);
        }
      }
    }
    handleCloseContextMenu();
  }, [contextMenu, deleteMutation, handleCloseContextMenu, hierarchyList]);

  const handleMove = useCallback(() => {
    // TODO: Implement move functionality
    handleCloseContextMenu();
  }, [handleCloseContextMenu]);

  const handleCreateProject = useCallback(() => {
    if (contextMenu?.node && onCreateProject) {
      onCreateProject(contextMenu.node.id);
      handleCloseContextMenu();
    }
  }, [contextMenu, onCreateProject, handleCloseContextMenu]);

  // Drag and drop handlers
  const handleDragStart = useCallback(
    (event: React.DragEvent, node: ProjectHierarchyType.AsObject) => {
      setDraggedNode(node);
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', node.id);
    },
    []
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent, node: ProjectHierarchyType.AsObject) => {
      event.preventDefault();
      if (draggedNode && draggedNode.id !== node.id) {
        setDragOverNode(node.id);
        event.dataTransfer.dropEffect = 'move';
      }
    },
    [draggedNode]
  );

  // const handleDragLeave = useCallback(() => {
  //   setDragOverNode(null);
  // }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent, targetNode: ProjectHierarchyType.AsObject) => {
      event.preventDefault();
      setDragOverNode(null);

      if (draggedNode && draggedNode.id !== targetNode.id) {
        // Prevent moving a node under its own descendant
        const isDescendant = (parentId: string, childId: string): boolean => {
          if (!hierarchyList) return false;
          const parent = hierarchyList.find(h => h.id === parentId);
          if (!parent) return false;
          if (parent.parentid === childId) return true;
          if (parent.parentid) return isDescendant(parent.parentid, childId);
          return false;
        };

        if (isDescendant(targetNode.id, draggedNode.id)) {
          console.warn('Cannot move a node under its own descendant');
          setDraggedNode(null);
          return;
        }

        try {
          // Use the update mutation to move the node
          await updateMutation.mutateAsync({
            id: draggedNode.id,
            parentid: targetNode.id,
            code: draggedNode.code,
            name: draggedNode.name,
            description: draggedNode.description,
            isactive: draggedNode.isactive,
          });
        } catch (error) {
          console.error('Error moving hierarchy item:', error);
        }
      }

      setDraggedNode(null);
    },
    [draggedNode, hierarchyList, updateMutation]
  );

  const handleNewFolder = useCallback(() => {
    handleOpenDialog('create', undefined, null);
  }, [handleOpenDialog]);

  // Cleanup anchor element on unmount
  React.useEffect(() => {
    return () => {
      cleanupAnchor();
    };
  }, [cleanupAnchor]);

  const renderTreeNode = useCallback(
    (
      node: ProjectHierarchyType.AsObject & { children?: ProjectHierarchyType.AsObject[] },
      level: number = 0
    ) => {
      const nodeProjects = getProjectsForNode(node.id);
      const hasChildren = Boolean(
        (node.children && node.children.length > 0) || nodeProjects.length > 0
      );
      const isExpanded = expandedNodes.has(node.id);
      const isSelected = selectedId === node.id;

      return (
        <React.Fragment key={node.id}>
          <ProjectHierarchyNode
            node={node}
            level={level}
            isExpanded={isExpanded}
            isSelected={isSelected}
            onToggleExpand={handleToggleExpand}
            onSelectNode={handleSelectNode}
            hasChildren={hasChildren}
            onContextMenu={handleContextMenu}
            onEllipsisClick={handleEllipsisClick}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            isDragOver={dragOverNode === node.id}
            isDragging={draggedNode?.id === node.id}
            projects={isExpanded ? nodeProjects : []}
            onProjectClick={onProjectClick}
            onProjectEdit={onProjectEdit}
          />
          {hasChildren &&
            isExpanded &&
            node.children?.map(child => renderTreeNode(child, level + 1))}
        </React.Fragment>
      );
    },
    [
      expandedNodes,
      selectedId,
      handleToggleExpand,
      handleSelectNode,
      handleContextMenu,
      handleEllipsisClick,
      handleDragStart,
      handleDragOver,
      handleDrop,
      dragOverNode,
      draggedNode,
      getProjectsForNode,
      onProjectClick,
      onProjectEdit,
    ]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Loading hierarchy...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center', color: theme.palette.error.main }}>
          <Typography variant="body1">Failed to load hierarchy</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        flex: '0 0 16.666%',
        maxWidth: '16.666%',
        minWidth: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            justifyContent: 'space-between',
          }}
        >
          {/* Collapse All */}
          <Button
            variant="outlined"
            onClick={handleCollapseAll}
            sx={{
              textTransform: 'none',
              minWidth: '32px',
              width: '32px',
              height: '40px',
              p: 0,
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }}
          >
            <ChevronsUp size={12} />
          </Button>

          {/* Search Bar */}
          <Box sx={{ position: 'relative', flex: 1 }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.palette.text.secondary,
                pointerEvents: 'none',
              }}
            />
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search folders..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  pl: 4,
                  pr: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>

          {/* New Folder Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewFolder}
            startIcon={<Plus size={16} />}
            sx={{
              textTransform: 'none',
              minWidth: 'auto',
              height: '40px',
              px: 2,
            }}
          >
            New Folder
          </Button>
        </Box>
      </Box>

      {/* Tree View */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {hierarchyTree.length > 0 ? (
          <Box
            sx={{
              '&:hover .MuiIconButton-root': { opacity: 1 },
              '& .MuiBox-root:hover .MuiIconButton-root': { opacity: 1 },
            }}
          >
            {hierarchyTree.map(node => renderTreeNode(node))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                color: theme.palette.text.secondary,
                px: 3,
                py: 4,
              }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                No folders found
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {searchTerm
                  ? `No items match "${searchTerm}"`
                  : 'Create a folder then add projects to it'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <ProjectHierarchyContextMenu
        anchorEl={anchorEl}
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        onAddSubfolder={handleAddSubfolder}
        onCreateProject={handleCreateProject}
        onEdit={handleEdit}
        onMove={handleMove}
        onDelete={handleDelete}
        nodeName={contextMenu?.node?.name}
        hasChildren={
          contextMenu?.node
            ? Boolean(hierarchyList?.some(h => h.parentid === contextMenu.node?.id))
            : false
        }
        hasProjects={
          contextMenu?.node
            ? Boolean(contextMenu.node.projectsList && contextMenu.node.projectsList.length > 0)
            : false
        }
      />

      {/* Dialog */}
      <ProjectHierarchyDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        mode={dialogMode}
        parentId={dialogParentId}
        node={dialogNode || undefined}
        availableParents={hierarchyList || []}
        userId={userId}
      />
    </Box>
  );
};
